import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { SERVICES, ADVANTAGES, CLIENTS, MEDIA } from "@/data/content";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.06 } }),
};

export function Problem() {
  return (
    <section id="chi-siamo" data-testid="problem-section" className="relative py-24 lg:py-32">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="font-accent text-xs uppercase tracking-[0.3em] text-teal-light">Il problema che risolviamo</span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white leading-tight">
              Non sai più cosa pubblicare? Non devi pensarci tu.
            </h2>
            <p className="mt-6 text-slate-300 leading-relaxed">
              Molte attività aprono un profilo social, pubblicano qualche contenuto e dopo poco
              tempo terminano le idee.
            </p>
            <p className="mt-4 text-slate-300 leading-relaxed">
              Altre pubblicano solamente fotografie, offerte e video tutti uguali, senza una
              strategia precisa e senza riuscire a comunicare realmente il valore dell'attività.
            </p>
            <p className="mt-4 text-slate-300 leading-relaxed">
              <span className="text-white font-medium">Fast Life Studio Agency</span> gestisce
              l'intero processo: studia il settore, sviluppa le idee, scrive gli script, organizza
              le riprese e realizza i contenuti. Il cliente deve concentrarsi sul proprio lavoro:
              alla comunicazione pensiamo noi.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              {["Niente più idee improvvisate", "Niente video tutti uguali", "Niente costi imprevisti"].map((t, i) => (
                <span key={i} data-testid={`problem-point-${i}`} className="rounded-full border border-teal-light/30 bg-teal/10 px-4 py-2 text-sm text-teal-light">
                  {t}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative"
          >
            <div className="absolute -inset-6 teal-glow rounded-[2rem]" />
            <img
              src={MEDIA.camera}
              alt="Attrezzatura professionale per riprese video"
              className="relative rounded-2xl border border-white/10 w-full h-[440px] object-cover"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export function Services() {
  return (
    <section id="servizi" data-testid="services-section" className="relative py-24 lg:py-32 bg-anthracite/40 border-y border-white/5">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="max-w-2xl">
          <span className="font-accent text-xs uppercase tracking-[0.3em] text-teal-light">Cosa facciamo</span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white leading-tight">
            Dall'idea alla pubblicazione. Ci occupiamo di tutto.
          </h2>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5 rounded-2xl overflow-hidden">
          {SERVICES.map((s, i) => {
            const Icon = Icons[s.icon] || Icons.Circle;
            return (
              <motion.div
                key={s.n}
                data-testid={`service-card-${s.n}`}
                variants={fadeUp}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="group bg-[#0b0b0b] p-8 lg:p-10 hover:bg-[#101413] transition-colors"
              >
                <div className="flex items-center justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal/15 text-teal-light group-hover:bg-teal-light group-hover:text-[#050505] transition-colors">
                    <Icon size={20} />
                  </span>
                  <span className="font-display text-3xl text-white/10 group-hover:text-teal-light/40 transition-colors">
                    {s.n}
                  </span>
                </div>
                <h3 className="mt-6 font-display text-xl text-white">{s.title}</h3>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function Advantages() {
  return (
    <section data-testid="advantages-section" className="relative py-24 lg:py-32">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="font-accent text-xs uppercase tracking-[0.3em] text-teal-light">Perché sceglierci</span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white leading-tight">
            Un unico servizio. Nessun pensiero.
          </h2>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 gap-4">
          {ADVANTAGES.map((a, i) => (
            <motion.div
              key={i}
              data-testid={`advantage-${i}`}
              variants={fadeUp}
              custom={i}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="flex items-start gap-4 rounded-xl border border-white/8 bg-[#0b0b0b] p-5 hover:border-teal-light/30 transition-colors"
            >
              <Icons.Check size={20} className="mt-0.5 shrink-0 text-teal-light" />
              <span className="text-slate-200">{a}</span>
            </motion.div>
          ))}
        </div>

        <p className="mt-10 mx-auto max-w-3xl text-center text-lg text-white leading-relaxed">
          Non affidiamo la tua comunicazione a una catena di montaggio.{" "}
          <span className="text-teal-light">Fernando segue direttamente</span> la strategia, le idee
          e il rapporto con l'attività.
        </p>
      </div>
    </section>
  );
}

export function ForWhom() {
  return (
    <section data-testid="forwhom-section" className="relative py-24 lg:py-32 bg-anthracite/40 border-y border-white/5">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <div className="max-w-3xl">
          <span className="font-accent text-xs uppercase tracking-[0.3em] text-teal-light">Per chi lavoriamo</span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white leading-tight">
            Ogni attività ha qualcosa da raccontare
          </h2>
          <p className="mt-5 text-slate-300 leading-relaxed">
            La strategia non viene copiata da un settore all'altro. Ogni attività riceve contenuti
            pensati in base al proprio pubblico, alla propria personalità e ai propri obiettivi.
          </p>
        </div>

        <div className="mt-12 flex flex-wrap gap-3">
          {CLIENTS.map((c, i) => (
            <motion.span
              key={c}
              data-testid={`client-tag-${i}`}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.04 }}
              className="rounded-full border border-white/12 bg-[#0b0b0b] px-5 py-2.5 text-sm text-slate-200 hover:border-teal-light/40 hover:text-teal-light transition-colors"
            >
              {c}
            </motion.span>
          ))}
        </div>
      </div>
    </section>
  );
}
